import { useAccount, usePublicClient, useWalletClient } from 'wagmi';
import { formatEther, formatUnits, parseEther, zeroAddress } from 'viem';
import { useEffect, useState } from 'react';
import { bullvsbear, erc20Address } from '../../../.config';
import BullvsBearABI from '../../../abi/bullvsbearABI.json';
import Swal from 'sweetalert2';
import { ethers, utils } from 'ethers';
import RoundsGrid from '../RoundsGrid';
import {useGetRounds } from '../context/PredictionsContext';

/* global BigInt */

const USDT_ABI = [
  {
    "inputs": [
      { "internalType": "address", "name": "spender", "type": "address" },
      { "internalType": "uint256", "name": "amount", "type": "uint256" }
    ],
    "name": "approve",
    "outputs": [{ "internalType": "bool", "name": "", "type": "bool" }],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [
      { "internalType": "address", "name": "owner", "type": "address" },
      { "internalType": "address", "name": "spender", "type": "address" }
    ],
    "name": "allowance",
    "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }],
    "stateMutability": "view",
    "type": "function"
  }
];

const CONTRACT_ADDRESS = bullvsbear;

export const useBullvsBearContract = () => {
  const { address } = useAccount();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const divBigInt = (a, b) => (b === 0n ? 0n : a / b);

  const fetchContractData = async () => {
    try {
      if (!publicClient) throw new Error("Public client not initialized");

      const currentEpoch = await publicClient.readContract({
        address: CONTRACT_ADDRESS,
        abi: BullvsBearABI,
        functionName: 'currentEpoch',
      });

      let oraclePrice = '0';
      try {
        const oracleData = await publicClient.readContract({
          address: CONTRACT_ADDRESS,
          abi: BullvsBearABI,
          functionName: 'getPriceFromOracle',
        });

        // oracleData is [roundId, price] as per function return
        oraclePrice = formatEther(oracleData[1]); // Get the price (second element)
        console.log('Oracle data:', oracleData, 'Formatted price:', oraclePrice);
      } catch (err) {
        console.error('Failed to get oracle price:', err);
        // Fallback to getting round data if oracle call fails
        const roundData = await publicClient.readContract({
          address: CONTRACT_ADDRESS,
          abi: BullvsBearABI,
          functionName: 'rounds',
          args: [currentEpoch]
        });
        oraclePrice = roundData?.lockPrice ? formatEther(roundData.lockPrice) : '0';
      }

      console.log('Current Epoch:', currentEpoch);
      console.log('Oracle Price:', oraclePrice);

      return {
        currentEpoch: Number(currentEpoch),
        oraclePrice
      };
    } catch (error) {
      console.error('Error fetching contract data:', error);
      return null;
    }
  };

  const checkAndApproveUSDT = async (amount) => {
    try {
      console.log("DEBUG: Starting approval process...");
      if (!walletClient || !address) {
        console.error("DEBUG: Wallet client or address missing", { walletClient, address });
        Swal.fire({
          icon: 'error',
          title: 'Wallet Not Connected',
          text: 'Please connect your wallet to continue'
        });
        throw new Error("Wallet not connected");
      }

      const usdtAddress = erc20Address;

      Swal.fire({
        title: 'Checking Approval',
        text: 'Checking your USDT allowance...',
        icon: 'info',
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      console.log("DEBUG: Checking allowance for", {
        usdtAddress,
        owner: address,
        spender: CONTRACT_ADDRESS
      });

      let allowance;
      try {
        allowance = await publicClient.readContract({
          address: usdtAddress,
          abi: USDT_ABI,
          functionName: 'allowance',
          args: [address, CONTRACT_ADDRESS]
        });
        console.log("DEBUG: Allowance result", allowance?.toString?.() || allowance);
      } catch (err) {
        console.error("DEBUG: Error reading allowance", err);
        throw new Error("Failed to check allowance: " + err.message);
      }

      const amountInWei = parseEther(amount.toString());
      console.log("DEBUG: Amount to approve (in wei):", amountInWei?.toString?.() || amountInWei);

      if (allowance < amountInWei) {
        Swal.fire({
          title: 'Approval Needed',
          text: 'Please approve USDT spending in your wallet...',
          icon: 'info',
          showConfirmButton: false,
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        try {
          console.log("DEBUG: Sending approval transaction");
          const hash = await walletClient.writeContract({
            address: usdtAddress,
            abi: USDT_ABI,
            functionName: 'approve',
            args: [CONTRACT_ADDRESS, BigInt(2) ** BigInt(256) - BigInt(1)]
          });
          console.log("DEBUG: Approval TX hash", hash);

          const receipt = await publicClient.waitForTransactionReceipt({ hash });
          console.log("DEBUG: Approval TX receipt", receipt);

          if (receipt.status === 'success' || receipt.status === 1) {
            Swal.fire({
              icon: 'success',
              title: 'Approved!',
              text: 'USDT spending approved successfully',
              timer: 2000
            });
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Approval Failed',
              text: 'Transaction failed on-chain. Please try again.'
            });
            return false;
          }
        } catch (err) {
          console.error("DEBUG: Error sending approval transaction", err);
          Swal.fire({
            icon: 'error',
            title: 'Approval Failed',
            text: err.message || 'Failed to approve USDT'
          });
          throw new Error("Token approval TX failed: " + (err.message || JSON.stringify(err)));
        }
      } else {
        console.log("DEBUG: No approval needed, allowance sufficient.");
      }

      return true;
    } catch (error) {
      console.error('DEBUG: Approval process failed:', error);
      Swal.fire({
        icon: 'error',
        title: 'Approval Failed',
        text: error.message || 'Failed to approve USDT'
      });
      return false;
    }
  };

  const betUp = async (amount, roundId, referrer = zeroAddress) => {
    try {
      if (!walletClient) {
        Swal.fire({
          icon: 'error',
          title: 'Wallet Not Connected',
          text: 'Please connect your wallet to place a bet'
        });
        throw new Error("Wallet not connected");
      }

        if (referrer && referrer !== zeroAddress) {
          // Basic address validation
          if (!/^0x[a-fA-F0-9]{40}$/.test(referrer)) {
            console.warn('Invalid referrer address, using zero address');
            referrer = zeroAddress;
          }
          console.log('Using referrer:', referrer);
        }

      const approved = await checkAndApproveUSDT(amount);
      if (!approved) throw new Error("USDT approval failed");

      Swal.fire({
        title: 'Placing Bet',
        text: 'Confirming your UP bet...',
        icon: 'info',
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const amountInWei = ethers.utils.parseUnits(amount.toString(), 6);
      const hash = await walletClient.writeContract({
        address: CONTRACT_ADDRESS,
        abi: BullvsBearABI,
        functionName: 'betBull',
        args: [amountInWei, referrer]
      });

      const receipt = await publicClient.waitForTransactionReceipt({ hash });

      if (receipt.status === 'success' || receipt.status === 1) {
        Swal.fire({
          icon: 'success',
          title: 'Bet Placed!',
          text: `Successfully bet ${amount} USDT UP on round ${roundId}`,
          timer: 3000
        });

        setTimeout(async () => {
          try {
            await fetchContractData();
            console.log('Contract data refreshed successfully');
          } catch (error) {
            console.error('Failed to refresh contract data:', error);
          }
        }, 2000);

      } else {
        Swal.fire({
          icon: 'error',
          title: 'Betting Failed',
          text: 'Transaction failed on-chain. Please try again.'
        });
        return { success: false, error: 'On-chain transaction failed' };
      }

      return {
        success: true,
        roundId,
        position: 'up',
        amount,
        hash
      };
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Betting Failed',
        text: error.message || 'Failed to place UP bet'
      });
      return { success: false, error: error.message };
    }
  };

  const betDown = async (amount, roundId, referrer = zeroAddress) => {
    try {
      if (!walletClient) {
        Swal.fire({
          icon: 'error',
          title: 'Wallet Not Connected',
          text: 'Please connect your wallet to place a bet'
        });
        throw new Error("Wallet not connected");
      }

      if (referrer && referrer !== zeroAddress) {
        // Basic address validation
        if (!/^0x[a-fA-F0-9]{40}$/.test(referrer)) {
          console.warn('Invalid referrer address, using zero address');
          referrer = zeroAddress;
        }
        console.log('Using referrer:', referrer);
      }

      const approved = await checkAndApproveUSDT(amount);
      if (!approved) throw new Error("USDT approval failed");

      Swal.fire({
        title: 'Placing Bet',
        text: 'Confirming your DOWN bet...',
        icon: 'info',
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const amountInWei = ethers.utils.parseUnits(amount.toString(), 6);
      const hash = await walletClient.writeContract({
        address: CONTRACT_ADDRESS,
        abi: BullvsBearABI,
        functionName: 'betBear',
        args: [amountInWei, referrer]
      });

      const receipt = await publicClient.waitForTransactionReceipt({ hash });

      if (receipt.status === 'success' || receipt.status === 1) {
        Swal.fire({
          icon: 'success',
          title: 'Bet Placed!',
          text: `Successfully bet ${amount} USDT DOWN on round ${roundId}`,
          timer: 3000
        });

        setTimeout(async () => {
          try {
            await fetchContractData();
            console.log('Contract data refreshed successfully');
          } catch (error) {
            console.error('Failed to refresh contract data:', error);
          }
        }, 2000);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Betting Failed',
          text: 'Transaction failed on-chain. Please try again.'
        });
        return { success: false, error: 'On-chain transaction failed' };
      }

      return {
        success: true,
        roundId,
        position: 'down',
        amount,
        hash
      };
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Betting Failed',
        text: error.message || 'Failed to place DOWN bet'
      });
      return { success: false, error: error.message };
    }
  };

const claimWinnings = async (roundId) => {
  try {
    if (!walletClient) {
      Swal.fire({
        icon: 'error',
        title: 'Wallet Not Connected',
        text: 'Please connect your wallet to claim'
      });
      throw new Error("Wallet not connected");
    }

    Swal.fire({
      title: 'Claiming',
      text: 'Claiming your winnings...',
      icon: 'info',
      showConfirmButton: false,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    const epochs = Array.isArray(roundId) ? roundId : [roundId];
    const epochsBigInt = epochs.map(epoch => BigInt(epoch));

    const hash = await walletClient.writeContract({
      address: CONTRACT_ADDRESS,
      abi: BullvsBearABI,
      functionName: 'claim',
      args: [epochsBigInt]
    });

    const receipt = await publicClient.waitForTransactionReceipt({ hash });

    if (receipt.status === 'success' || receipt.status === 1) {
      Swal.fire({
        icon: 'success',
        title: 'Claimed!',
        text: `Successfully claimed winnings for rounds ${epochs.join(', ')}`,
        timer: 3000
      });

      // Refresh múltiplo com delay maior para garantir que o contrato processou
      setTimeout(async () => {
        try {
          console.log('Refreshing data after successful claim...');
          await fetchContractData();
          
          // Força refresh específico dos useUserBet para as epochs claimadas
          epochs.forEach(epoch => {
            console.log(`Triggering refresh for epoch ${epoch}`);
            // Força uma re-renderização dos componentes que usam useUserBet para esta epoch
            window.dispatchEvent(new CustomEvent('userBetRefresh', { 
              detail: { epoch: epoch.toString() } 
            }));
          });
          
          console.log('All data refreshed successfully');
        } catch (error) {
          console.error('Failed to refresh data:', error);
        }
      }, 2000); // Aumentar delay para 2 segundos

    } else {
      Swal.fire({
        icon: 'error',
        title: 'Claim Failed',
        text: 'Transaction failed on-chain. Please try again.'
      });
      return { success: false, error: 'On-chain transaction failed' };
    }

    return {
      success: true,
      claimedRounds: epochs,
      hash
    };
  } catch (error) {
    Swal.fire({
      icon: 'error',
      title: 'Claim Failed',
      text: error.message || 'Failed to claim winnings'
    });
    return { success: false, error: error.message };
  }
};

  // Fetch user history from the contract
  const fetchUserHistory = async (userAddress) => {
    try {
      if (!publicClient || !userAddress) return [];

      const userRounds = await publicClient.readContract({
        address: CONTRACT_ADDRESS,
        abi: BullvsBearABI,
        functionName: 'getUserRounds',
        args: [userAddress, 0n, 50n]
      });

      if (!userRounds || !userRounds[0]) return [];

      const history = [];

      for (let i = 0; i < userRounds[0].length; i++) {
        const epoch = Number(userRounds[0][i]);
        const bet = userRounds[1][i];
        const amount = bet.amount;
        // Only process real bets
        if (amount === 0n || epoch === 0) continue;

        // Fetch round data
        const round = await publicClient.readContract({
          address: CONTRACT_ADDRESS,
          abi: BullvsBearABI,
          functionName: 'rounds',
          args: [BigInt(epoch)]
        });

        // Safely extract fields as BigInt
        const lockPrice = BigInt(round.lockPrice);
        const closePrice = BigInt(round.closePrice);
        const rewardBaseCalAmount = BigInt(round.rewardBaseCalAmount);
        const rewardAmount = BigInt(round.rewardAmount);
        const oracleCalled = round.oracleCalled;
        const userPos = bet.position ? 'up' : 'down';

        // Determine win/loss
        let won = false;
        let payout = '0';
        if (
          oracleCalled &&
          ((closePrice > lockPrice && userPos === 'up') ||
            (closePrice < lockPrice && userPos === 'down'))
        ) {
          won = true;
          // Payout = (betAmount / rewardBaseCalAmount) * rewardAmount
          if (rewardBaseCalAmount > 0n) {
            // Use extra precision as in contract (mul by 1e12 for precision, then div by 1e12 at end)
            const proportion = divBigInt(amount * 1000000000000n, rewardBaseCalAmount);
            const addedReward = divBigInt(rewardAmount * proportion, 1000000000000n);
            payout = formatUnits(addedReward, 6);
          }
        }

        history.push({
          epoch,
          position: userPos,
          amount: formatUnits(amount, 6),
          claimed: bet.claimed,
          won,
          payout
        });
      }

      return history;
    } catch (error) {
      console.error('Failed to fetch user history:', error);
      return [];
    }
  };

  const useUserBet = (epoch) => {
    const [betInfo, setBetInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const { address } = useAccount();
    const publicClient = usePublicClient();
    const rounds = useGetRounds();
    useEffect(() => {
      const fetchBetInfo = async () => {
        setLoading(true);
        if (!address || typeof epoch === 'undefined' || epoch === null || !publicClient) {
          setBetInfo(null);
          setLoading(false);
          // console.warn('No address, epoch, or publicClient; skipping fetchBetInfo for epoch:', epoch);
          return;
        }

        try {
          // console.log(`Fetching bet info for address ${address} on epoch ${epoch}`);
          const userBetData = await publicClient.readContract({
            address: bullvsbear,
            abi: BullvsBearABI,
            functionName: 'ledger',
            args: [BigInt(epoch), address]
          });

          // console.log(`Raw bet info from contract for epoch ${epoch}:`, userBetData);

          if (userBetData && userBetData[1] && userBetData[1] > 0n) { // Check amount is valid and > 0
            // console.log(`User has a bet for epoch ${epoch}. Amount: ${userBetData[1].toString()}`);
            setBetInfo({
              position: userBetData[0] === 0 ? 'up' : 'down', // 0 for Bull (up), 1 for Bear (down)
              amount: userBetData[1].toString(), // Return the raw BigInt string for amount
              claimed: userBetData[2] // This is ledger.claimed
            });
          } else {
            // console.log(`No bet found or amount is zero for user ${address} on epoch ${epoch}`);
            setBetInfo(null);
          }
        } catch (error) {
          // console.error(`Error fetching user bet for epoch ${epoch}:`, error);
          setBetInfo(null);
        } finally {
          setLoading(false);
        }
      };

      fetchBetInfo();
    }, [address, epoch, rounds, publicClient]);

    return betInfo; // Directly return betInfo, components will check for null
  };

  return {
    betUp,
    betDown,
    claimWinnings,
    fetchContractData,
    checkAndApproveUSDT,
    fetchUserHistory,
    useUserBet,
    address
  };
};

export default useBullvsBearContract;